"use client";
import { FunctionComponent, useId, useMemo } from "react";
import Button from "@/components/design-system/button";

import AuthorizedDistributorBadge from "@/components/authorized-distributor-badge/authorizedDistributorBadge";
import Badge from "@/components/design-system/badge";
import LicenseRequiredBadge from "@/components/license-required-badge/licenseRequiredBadge";
import { useShowLicenseRequiredBadge } from "@/app/hooks/useLicenseRequiredBadge";
import { licenseIsRequired } from "@/utils/licenseRequired";
import CurrencyDisplay from "@/app/components/currencyDisplay";
import checkAddToCart, { AddToCartStatus } from "@/app/ec/product/[basePath]/checkAddToCart";
import PriceBreakSummary from "@/app/ec/product/[basePath]/priceBreakSummary";
import StarRating from "@/components/design-system/star-rating";
import StockAndShippingMessage from "@/app/ec/product/[basePath]/stockAndShippingMessage";
import Tooltip from "@/components/design-system/tooltip";
import getVendorNameForURL from "@/app/vendor/utils/getVendorNameForURL";
import Link from "next/link";
import styles from "./vendorOption.module.scss";

const VendorOption: FunctionComponent<VendorOptionProps> = ({
  vendorOption,
  index,
  addToCartCallback,
  productBeingAddedToCart,
  vendorProductBeingAddedToCart,
  manufacturerOrCategoryLicenseIsRequired,
  quantity,
}) => {
  const vendorNameForURL = useMemo<string>(() => {
    return getVendorNameForURL(vendorOption.vendorName);
  }, [vendorOption.vendorName]);

  const addToCartStatus = checkAddToCart(quantity, vendorOption.minimumQuantityPurchasable, vendorOption.inventory);
  const quantityIsAddable = addToCartStatus === AddToCartStatus.OK;

  const showLicenseRequiredBadge = useShowLicenseRequiredBadge(
    licenseIsRequired(vendorOption, manufacturerOrCategoryLicenseIsRequired),
  );
  const thisVendorProductIsBeingAddedToCart = vendorProductBeingAddedToCart === vendorOption.vendorProductId;

  // The PDP mounts the vendor list more than once, so the id has to be per instance.
  const quantityRequirementId = useId();

  const quantityRequirement =
    addToCartStatus === AddToCartStatus.MINIMUM_QUANTITY
      ? `Minimum Order Quantity for this vendor is ${vendorOption.minimumQuantityPurchasable}. Enter a larger quantity or select a different vendor.`
      : `Quantity exceeds ${vendorOption.vendorName}'s stock of ${vendorOption.inventory} item(s). Enter a smaller quantity or select a different vendor.`;

  const addToCartButton = (
    <Button
      variant={index == 0 ? "primary" : "secondary"}
      color={index == 0 ? "teal" : "blue"}
      size="small"
      text="Add to Cart"
      showLeftIcon={false}
      showRightIcon={false}
      className="vendor-add-to-cart-button"
      loading={thisVendorProductIsBeingAddedToCart}
      disabled={thisVendorProductIsBeingAddedToCart || !quantityIsAddable}
      ariaDescribedBy={quantityIsAddable ? undefined : quantityRequirementId}
      onClick={(e) => {
        e.preventDefault();

        if (productBeingAddedToCart === false) {
          addToCartCallback(vendorOption);
        }
      }}
    />
  );

  return (
    <div className={`vendor-row ${index == 0 ? "chosen-vendor" : ""}`}>
      <div className="vendor-body-info">
        <div>
          <Link
            className="vendor-info"
            title={`Vendor Product Code (VP Code): ${vendorOption.vendorProductCode}`}
            href={`/vendor/${vendorNameForURL}/${vendorOption.vendorId}`}
            prefetch={false}
          >
            <span className="vendor-name">{vendorOption.vendorName}</span>

            {vendorOption.vdrNumberOfGeneralRatings > 0 && (
              <span className="vendor-rating-row">
                <span className="vendor-entry-rating-container">
                  <StarRating
                    rating={vendorOption.vendorRating}
                    size="small"
                    aria-label={`${vendorOption.vendorRating} out of 5 stars`}
                  />
                </span>
                <span className="vendor-entry-rating-value">
                  {vendorOption.vendorRating.toFixed(1)}
                  <span className="vendor-entry-rating-scale">/5</span>
                </span>
                <span className="vendor-entry-rating-count">({vendorOption.vdrNumberOfGeneralRatings})</span>
              </span>
            )}
          </Link>
        </div>

        <StockAndShippingMessage
          vendorOption={vendorOption}
          stockMessageClassName={styles.stockMessage}
          businessDaysClassName="fw-bold"
          className="vendor-stock-and-shipping-message sm"
          showArrivalDateVariation
        />

        {/* {vendorOption.isBackordered && <span className="backordered-vendor-product">Backordered</span>} */}

        {vendorOption.isAuthorizedDistributor && (
          <div className={styles.authorizedDistributorBadge}>
            <AuthorizedDistributorBadge />
          </div>
        )}

        {showLicenseRequiredBadge && (
          <div className={styles.licenseRequiredBadge}>
            <LicenseRequiredBadge />
          </div>
        )}
      </div>
      <div className="vendor-body-pricing">
        <PriceBreakSummary
          vendorOption={vendorOption}
          eachClassName={`.vendor-shipping ${index == 0 ? "vendor-shipping-bold" : ""}`}
          shippingClassName={`.vendor-shipping ${index == 0 ? "vendor-shipping-bold" : ""}`}
          heavyShippingClassName="vendor-heavy-shipping"
          className="vendor-price-break-summary"
          showAddMoreFreeShipping
          addMoreFreeShippingClassName="vendor-add-more-free-shipping"
          priceBreaksClassName="vendor-price-breaks"
        />
      </div>
      <div className="vendor-body-total">
        <span className="vendor-total-value">
          <CurrencyDisplay value={vendorOption.productGrandTotal} />
        </span>

        {vendorOption.isLowestPrice === true && (
          <Badge
            label="Lowest Price"
            color="magenta"
            showIcon={false}
            strong={false}
            className="vendor-lowest-price-badge"
          />
        )}
      </div>

      <div className="vendor-body-add-to-cart">
        {quantityIsAddable ? (
          addToCartButton
        ) : (
          <Tooltip
            trigger="click"
            label="Quantity requirement"
            content={<span className={styles.quantityRequirement}>{quantityRequirement}</span>}
          >
            {addToCartButton}
          </Tooltip>
        )}

        {!quantityIsAddable && (
          <span className="vendor-product-min-qty" id={quantityRequirementId}>
            {addToCartStatus === AddToCartStatus.MINIMUM_QUANTITY
              ? `Min qty: ${vendorOption.minimumQuantityPurchasable}`
              : `Max qty: ${vendorOption.inventory}`}
          </span>
        )}
      </div>
    </div>
  );
};

type VendorOptionProps = {
  vendorOption: VendorOptionState;
  index: number;
  // eslint-disable-next-line no-unused-vars
  addToCartCallback: (vendorProductToAdd: VendorOptionState) => void;
  manufacturerOrCategoryLicenseIsRequired: boolean;
  productBeingAddedToCart: boolean;
  vendorProductBeingAddedToCart: null | number;
  quantity: number;
  // showManufacturerPromoModal: React.Dispatch<React.SetStateAction<boolean>>
};
export default VendorOption;
